import { Signal } from "signals";
import { MenuSection } from "slotworks/components/betbar/menus/menu-section";
import GeneralOptions from "slotworks/components/betbar/vo/general-options";
import SliderComponent from "slotworks/components/ui/slider-component";
import FlexiSlider from "slotworks/graphics/elements/flexi-slider";
import FlexiText from "slotworks/graphics/elements/flexi-text";
import FlexiToggle, { ToggleEvent } from "slotworks/graphics/elements/flexi-toggle";
import SliderElement from "slotworks/graphics/elements/slider-element";
import Layers from "slotworks/graphics/layers/layers";
import SoundManager from "slotworks/sound/sound-manager";
import { formatMinutes } from "slotworks/utils/string-formatters";
import settingsModel from "slotworks/model/settings/settings-model";
import Components from "slotworks/components/components";

export default class GeneralSettingsSection extends MenuSection {

    protected options: GeneralOptions;

    private sessionLimitSlider: SliderComponent;

    private quickSpinToggle: FlexiToggle;
    private displayCoinsToggle: FlexiToggle;
    private showIntroToggle: FlexiToggle;
    private leftHandedToggle: FlexiToggle;
    private spaceSpinToggle: FlexiToggle;

    private soundFxToggle: FlexiToggle;
    private musicToggle: FlexiToggle;

    private volumeText: FlexiText;
    private volumeSlider: SliderElement;

    constructor(signal: Signal, initialValues: GeneralOptions) {
        super(signal, initialValues);

        // Session limit slider
        const sessionLimitFlexiSlider = Layers.MenusContent.getFlexiSlider("session_limit");
        const sessionLimitText = Layers.MenusContent.getFlexiText("session_limit_label");
        const sessionLimitMinText = Layers.MenusContent.getFlexiText("session_limit_min_label");
        const sessionLimitMaxText = Layers.MenusContent.getFlexiText("session_limit_max_label");

        // Canvas slider
        const sessionLimitTrack = Layers.MenusContent.getSprite("session_limit_track");
        const sessionLimitFill = Layers.MenusContent.getSprite("session_limit_fill");
        const sessionLimitHandle = Layers.MenusContent.getSprite("session_limit_handle");

        if (sessionLimitFlexiSlider) {
            this.sessionLimitSlider = new SliderComponent(sessionLimitFlexiSlider, sessionLimitText, sessionLimitMinText, sessionLimitMaxText);
        } else if (sessionLimitHandle) {
            // TODO: This should automatically be added as part of layers / assetworks
            const sliderElement = new SliderElement(sessionLimitTrack, sessionLimitFill,
                this.options.sessionLimitMin, this.options.sessionLimitMax, this.options.sessionLimitStep, sessionLimitHandle);
            const flexiSlider = new FlexiSlider();
            flexiSlider.addTarget(sliderElement);
            this.sessionLimitSlider = new SliderComponent(flexiSlider, sessionLimitText, sessionLimitMinText, sessionLimitMaxText);
        }

        if (this.sessionLimitSlider) {
            this.sessionLimitSlider.setMin(this.options.sessionLimitMin);
            this.sessionLimitSlider.setMax(this.options.sessionLimitMax);
            this.sessionLimitSlider.formatFunction = formatMinutes;
            this.sessionLimitSlider.setValue(this.options.sessionLimit);

            this.sessionLimitSlider.onChange.add(this.onSessionLimitChangeHandler);
        }

        // Settings Toggles
        this.quickSpinToggle = Layers.MenusContent.getFlexiToggle("quick_spin");
        if (this.quickSpinToggle) {
            if(settingsModel.isQuickSpinDisabled()) {
                this.quickSpinToggle.setValue(false);
                this.quickSpinToggle.setEnabled(false);
                const quickSpinToggleOff = Layers.MenusContent.getSprite("btn-quick_spinoff");
                if(quickSpinToggleOff) {
                    quickSpinToggleOff.alpha = 0.4;
                }
                const text = Layers.MenusContent.getFlexiText("quick_spin_on");
                if(text) {
                    text.setAlpha(0.5);
                }
            } else {
                this.quickSpinToggle.setValue(this.options.quickSpin);

                this.quickSpinToggle.on(ToggleEvent.CHANGE, this.quickSpinToggleHandler);

                Components.betBar.onQuickSpinUpdated.add(this.onQuickSpinUpdated);
            }
        }

        this.displayCoinsToggle = Layers.MenusContent.getFlexiToggle("display_coins");
        if (this.displayCoinsToggle) {
            this.displayCoinsToggle.setValue(this.options.displayCoins);

            this.displayCoinsToggle.on(ToggleEvent.CHANGE, this.displayCoinsToggleHandler);
        }

        this.showIntroToggle = Layers.MenusContent.getFlexiToggle("show_intro");
        if (this.showIntroToggle) {
            this.showIntroToggle.setValue(this.options.showIntro);

            this.showIntroToggle.on(ToggleEvent.CHANGE, this.showIntroToggleHandler);
        }

        this.leftHandedToggle = Layers.MenusContent.getFlexiToggle("left_handed");
        if (this.leftHandedToggle) {
            this.leftHandedToggle.setValue(this.options.leftHanded);

            this.leftHandedToggle.on(ToggleEvent.CHANGE, this.leftHandedToggleHandler);
        }

        this.spaceSpinToggle = Layers.MenusContent.getFlexiToggle("space_spin");
        if (this.spaceSpinToggle) {
            this.spaceSpinToggle.setValue(this.options.spaceToSpin);

            this.spaceSpinToggle.on(ToggleEvent.CHANGE, this.spaceSpinToggleHandler);
        }

         // Sound toggles
         this.soundFxToggle = Layers.MenusContent.getFlexiToggle("soundfx");
         if (this.soundFxToggle) {
             this.soundFxToggle.setValue(!SoundManager.getSoundFxMuted() && !SoundManager.getMuted());
 
             this.soundFxToggle.on(ToggleEvent.CHANGE, this.soundFxToggleHandler);
         }
 
         this.musicToggle = Layers.MenusContent.getFlexiToggle("music");
         if (this.musicToggle) {
             this.musicToggle.setValue(!SoundManager.getMusicMuted() && !SoundManager.getMuted());
 
             this.musicToggle.on(ToggleEvent.CHANGE, this.musicToggleHandler);
         }
 
        SoundManager.onMuteChange.add(this.onMuteChange);

        // Volume slider
        const volumeTrack = Layers.MenusContent.getSprite("volume_track");
        const volumeFill = Layers.MenusContent.getSprite("volume_fill");
        const volumeHandle = Layers.MenusContent.getSprite("volume_handle");

        this.volumeText = Layers.MenusContent.getFlexiText("volume_value");

        if (volumeTrack && volumeFill) {
            this.volumeSlider = new SliderElement(volumeTrack, volumeFill, 0, 100, 1, volumeHandle);
            this.volumeSlider.onInput.add(this.updateVolume);

            const volume = SoundManager.getVolume() * 100;
            this.volumeSlider.setValue(volume);
            this.updateVolume(volume);
        }
    }

    private onMuteChange = (muted: boolean) => {
        if (this.musicToggle) {
            this.musicToggle.setValue(!(SoundManager.getMusicMuted() || muted));
        }

        if (this.soundFxToggle) {
            this.soundFxToggle.setValue(!(SoundManager.getSoundFxMuted() || muted));
        }
    }

    public destroy() {
        if (this.sessionLimitSlider) {
            this.sessionLimitSlider.onChange.remove(this.onSessionLimitChangeHandler);
        }

        if (this.quickSpinToggle) {
            this.quickSpinToggle.off(ToggleEvent.CHANGE, this.quickSpinToggleHandler);

            Components.betBar.onQuickSpinUpdated.remove(this.onQuickSpinUpdated);
        }

        if (this.displayCoinsToggle) {
            this.displayCoinsToggle.off(ToggleEvent.CHANGE, this.displayCoinsToggleHandler);
        }

        if (this.showIntroToggle) {
            this.showIntroToggle.off(ToggleEvent.CHANGE, this.showIntroToggleHandler);
        }

        if (this.leftHandedToggle) {
            this.leftHandedToggle.off(ToggleEvent.CHANGE, this.leftHandedToggleHandler);
        }

        if (this.spaceSpinToggle) {
            this.spaceSpinToggle.off(ToggleEvent.CHANGE, this.spaceSpinToggleHandler);
        }

        if (this.soundFxToggle) {
            this.soundFxToggle.off(ToggleEvent.CHANGE, this.soundFxToggleHandler);
        }

        if (this.musicToggle) {
            this.musicToggle.off(ToggleEvent.CHANGE, this.musicToggleHandler);
        }

        if (this.volumeSlider) {
            this.volumeSlider.onInput.remove(this.updateVolume);
        }

        super.destroy();
    }

    private onSessionLimitChangeHandler = (value: number) => {
        this.options.sessionLimit = value;

        this.sendOptions();
    }

    private quickSpinToggleHandler = () => {
        this.options.quickSpin = this.quickSpinToggle.getValue();

        this.sendOptions();
    }

    private displayCoinsToggleHandler = () => {
        this.options.displayCoins = this.displayCoinsToggle.getValue();

        this.sendOptions();
    }

    private showIntroToggleHandler = () => {
        this.options.showIntro = this.showIntroToggle.getValue();

        this.sendOptions();
    }

    private leftHandedToggleHandler = () => {
        this.options.leftHanded = this.leftHandedToggle.getValue();

        this.sendOptions();
    }

    private spaceSpinToggleHandler = () => {
        this.options.spaceToSpin = this.spaceSpinToggle.getValue();

        this.sendOptions();
    }

    private soundFxToggleHandler = () => {
        const isSoundMuted = this.soundFxToggle && !this.soundFxToggle.getValue();
        const isMusicMuted = this.musicToggle && !this.musicToggle.getValue();
        const shouldChangeGlobalState = isSoundMuted && isMusicMuted;

        SoundManager.setSoundFxMute(isSoundMuted);

        if (SoundManager.getMuted() !== shouldChangeGlobalState) {
            const muteUnmute = shouldChangeGlobalState ? SoundManager.mute : SoundManager.unmute;
            muteUnmute();
        }
    }

    private musicToggleHandler = () => {
        const isSoundMuted = this.soundFxToggle && !this.soundFxToggle.getValue();
        const isMusicMuted = this.musicToggle && !this.musicToggle.getValue();
        const shouldChangeGlobalState = isSoundMuted && isMusicMuted;

        SoundManager.setMusicMute(isMusicMuted);

        if (SoundManager.getMuted() !== shouldChangeGlobalState) {
            const muteUnmute = shouldChangeGlobalState ? SoundManager.mute : SoundManager.unmute;
            muteUnmute();
        }
    }

    private updateVolume = (volume: number) => {
        SoundManager.setVolume(volume / 100);

        if (this.volumeText) {
            this.volumeText.setText(volume.toString());
        }
    }

    private onQuickSpinUpdated = (quickSpin: boolean) => {
        this.options.quickSpin = quickSpin;

        if(this.quickSpinToggle) {
            this.quickSpinToggle.setValue(this.options.quickSpin);
        }
    }
}



// WEBPACK FOOTER //
// ./~/slotworks/components/betbar/menus/general-section.ts